<script setup lang="ts">
import { Megaphone } from '@lucide/vue'

import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'

import { useCommunicationFormatters } from '../../composables/useCommunicationFormatters'
import { useCommunicationTranslations } from '../../composables/useCommunicationTranslations'
import type { CommunicationAttachment, CommunicationItem, CommunicationSender } from '../../types/communication'

import AnnouncementStatusBadges from './AnnouncementStatusBadges.vue'
import SubtypeHeader from './SubtypeHeader.vue'

/**
 * Shared header for Communication envelope views.
 *
 * Single source of truth for the "hero" block rendered above the message body
 * across: Inbox detail, Communication detail page, PunchClock gate modal, and
 * any other surface that renders a {@link CommunicationItem}. Replaces the
 * inline markup that used to be duplicated inside `AnnouncementDetail.vue`.
 */
defineProps<{
  item: CommunicationItem | null
  /** Lightweight fallback when the caller only has a Communication DTO (e.g. responder view). */
  communication?: {
    title: string
    sender: CommunicationSender | null
    sent_at: string | null
    attachments: CommunicationAttachment[]
  }
}>()

const { formatFullDate, getPriorityClasses } = useCommunicationFormatters()
const { tm } = useCommunicationTranslations('communication')

function getInitials(name: string): string {
  return name
    .split(' ')
    .map(part => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()
}
</script>

<template>
  <div class="min-w-0 shrink-0 space-y-3 [overflow-wrap:anywhere]">
    <!-- Type + Priority row -->
    <div class="flex min-w-0 flex-wrap items-center gap-2">
      <div class="flex size-8 items-center justify-center rounded-full bg-primary/10">
        <Megaphone class="size-4 text-primary" />
      </div>
      <Badge
        v-if="item"
        variant="secondary"
        :class="[getPriorityClasses(item.priority), 'text-[10px] font-bold uppercase']"
      >
        {{ tm(`priorities.${item.priority}`) }}
      </Badge>
      <span class="text-xs uppercase tracking-wider text-muted-foreground">
        {{ tm(`types.${item?.type || 'announcement'}`) }}
      </span>
    </div>

    <!-- Title -->
    <h1 class="min-w-0 break-words text-xl font-bold leading-snug text-foreground">
      {{ item?.title || communication?.title }}
    </h1>

    <!-- Sender + Dates -->
    <div class="flex flex-wrap items-center gap-3">
      <div v-if="item?.sender || communication?.sender" class="flex items-center gap-2">
        <Avatar class="size-7">
          <AvatarFallback class="text-[10px]">
            {{ getInitials((item?.sender || communication?.sender)!.name) }}
          </AvatarFallback>
        </Avatar>
        <span class="text-sm font-medium text-foreground">
          {{ (item?.sender || communication?.sender)!.name }}
        </span>
      </div>

      <Separator v-if="item?.sender || communication?.sender" orientation="vertical" class="h-4" />

      <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span v-if="item?.sent_at || communication?.sent_at">
          {{ tm('detail.sent') }} {{ formatFullDate(item?.sent_at || communication?.sent_at || null) }}
        </span>
        <span v-if="item?.read_at">
          {{ tm('detail.read') }} {{ formatFullDate(item.read_at) }}
        </span>
      </div>
    </div>

    <!-- Status badges -->
    <AnnouncementStatusBadges
      v-if="item"
      :acknowledgment-level="item.acknowledgment_level"
      :is-pending-acknowledgment="item.is_pending_acknowledgment"
      :is-pending-signature="item.is_pending_signature"
    />

    <!-- Subtype-specific header (UserNotice date range, disciplinary client, …) -->
    <SubtypeHeader v-if="item?.subtype_header" :payload="item.subtype_header" />
  </div>
</template>
